import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { translation } from '@cue/translations';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import { cueClose, cueDelete } from '@cue/ui/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent } from '@cue/modal';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

type DeleteScope = 'event' | 'occurrence' | 'series';

type ReservationDeleteDialogData = {
  reservation: any;
  scope?: DeleteScope;
  titleKey?: string;
  closeKey?: string;
  questionKeys?: Partial<Record<DeleteScope, string>>;
  confirmButtonKeys?: Partial<Record<DeleteScope, string>>;
};

@Component({
  selector: 'cue-reservation-delete',
  templateUrl: './reservation-delete.component.html',
  styleUrls: ['./reservation-delete.component.scss'],
  imports: [
    TranslocoPipe,
    ModalComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    ModalHeaderComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationDeleteComponent {
  translation = translation;
  modal = inject(DialogRef);
  dialogData = inject<any | ReservationDeleteDialogData>(DIALOG_DATA);
  reservation = computed(() => ('reservation' in this.dialogData ? this.dialogData.reservation : this.dialogData));
  deleteScope = computed<DeleteScope>(() => ('scope' in this.dialogData ? this.dialogData.scope : ('event' as const)));
  titleKey = computed(() =>
    'titleKey' in this.dialogData && this.dialogData.titleKey
      ? this.dialogData.titleKey
      : this.translation.deletePopup.title
  );
  closeKey = computed(() =>
    'closeKey' in this.dialogData && this.dialogData.closeKey
      ? this.dialogData.closeKey
      : this.translation.deletePopup.close
  );

  questionKey = computed(() => {
    if ('questionKeys' in this.dialogData && this.dialogData.questionKeys?.[this.deleteScope()]) {
      return this.dialogData.questionKeys[this.deleteScope()];
    }

    switch (this.deleteScope()) {
      case 'occurrence':
        return this.translation.deletePopup.questionOccurrence;
      case 'series':
        return this.translation.deletePopup.questionSeries;
      default:
        return this.translation.deletePopup.question;
    }
  });

  confirmButtonKey = computed(() => {
    if ('confirmButtonKeys' in this.dialogData && this.dialogData.confirmButtonKeys?.[this.deleteScope()]) {
      return this.dialogData.confirmButtonKeys[this.deleteScope()];
    }

    switch (this.deleteScope()) {
      case 'occurrence':
        return this.translation.reservationPopup.deleteOccurrence;
      case 'series':
        return this.translation.reservationPopup.deleteSeries;
      default:
        return this.translation.deletePopup.delete;
    }
  });
  cueClose = cueClose;
  cueDelete = cueDelete;
}
